import React, { useCallback, useEffect, useMemo, useState } from 'react';
import EquipmentEditor from './EquipmentEditor';
import SetupPanel from './SetupPanel';
import EquipmentIdentityLinks from './EquipmentIdentityLinks';
import HdmiInputAssignmentBoard from './HdmiInputAssignmentBoard';
import { isAudioEquipment } from '../../utils/equipment';
import { toggleTargetGroupMember } from '../VenueTargets/targetGroupUtils';

const emptyScreen = () => ({
  name: '',
  displayKind: 'managed',
  televisionDeviceId: '',
  players: [],
});

const emptyAudio = () => ({ name: '', sourceDeviceId: '', logicalScreenId: '' });
const emptyTargetGroup = () => ({
  id: null,
  name: '',
  description: '',
  screenIds: [],
  audioEndpointIds: [],
  isSystem: false,
  isDraft: true,
});
const commonInputs = [
  'HDMI 1', 'HDMI 2', 'HDMI 3', 'HDMI 4', 'HDMI 5', 'HDMI 6',
  'DisplayPort (DP)', 'Mini DisplayPort', 'USB-C', 'VGA', 'DVI',
  'Component', 'Composite / AV', 'Antenna / TV',
];

const isRemoteControlDevice = (device) => [
  'staff', 'phone remote', 'staff phone', 'phone_remote', 'staff_phone',
  'control console', 'console remote', 'control_console', 'console_remote',
].includes(String(device?.type || '').trim().toLowerCase());

function PortSelector({ value, onChange }) {
  const isCustom = Boolean(value) && !commonInputs.includes(value);
  return (
    <div className="topology-setup__port">
      <select
        aria-label="TV input"
        value={!value ? '' : (isCustom ? 'custom' : value)}
        onChange={(event) => onChange(event.target.value === 'custom' ? 'Custom' : event.target.value)}
      >
        <option value="">Select TV input</option>
        {commonInputs.map((input) => <option key={input} value={input}>{input}</option>)}
        <option value="custom">Custom…</option>
      </select>
      {isCustom && (
        <input value={value === 'Custom' ? '' : value} onChange={(event) => onChange(event.target.value || 'Custom')} placeholder="Custom port" />
      )}
    </div>
  );
}

function LogicalTopologySetup({ areaId, devices, currentTopology, onTopologyChange, tvInputReports = {} }) {
  const [topology, setTopology] = useState({ screens: [], audioEndpoints: [] });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [equipment, setEquipment] = useState([]);
  const handleEquipmentChange = useCallback((entries, changedDevice) => {
    setEquipment((current) => [...new Map([...current, ...entries].map((entry) => [Number(entry.id), entry])).values()]);
    setTopology((current) => ({ ...current, screens: current.screens.map((screen) => ({ ...screen,
      players: (screen.players || []).map((player) => {
        const device = entries.find((entry) => Number(entry.id) === Number(player.deviceId || player.id));
        if (isAudioEquipment(device)) return { ...player, sourceRole: 'audio_system' };
        return device && Number(changedDevice?.id) === Number(device.id) ? { ...player, sourceRole: 'video_source' } : player;
      }),
    })) }));
  }, []);
  const token = localStorage.getItem('token');

  useEffect(() => {
    if (currentTopology?.screens) setTopology(currentTopology);
  }, [currentTopology]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetch(`/api/areas/${areaId}/topology`, { headers: { Authorization: `Bearer ${token}` } })
      .then(async (response) => {
        const data = await response.json();
        if (!response.ok) throw new Error(data.message || 'Could not load topology');
        if (!cancelled) {
          setTopology(data);
          onTopologyChange?.(data);
        }
      })
      .catch((error) => !cancelled && setMessage(error.message))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [areaId, token, onTopologyChange]);

  const deviceOptions = useMemo(
    () => [...new Map([...devices, ...equipment].map((device) => [Number(device.id), device])).values()]
      .filter((device) => device.id && !isRemoteControlDevice(device)),
    [devices, equipment]
  );
  const updateScreen = (index, field, value) => setTopology((current) => ({
    ...current,
    screens: current.screens.map((screen, screenIndex) => screenIndex === index
      ? { ...screen, [field]: value }
      : screen),
  }));
  const updateAudio = (index, field, value) => setTopology((current) => ({
    ...current,
    audioEndpoints: current.audioEndpoints.map((endpoint, endpointIndex) => endpointIndex === index
      ? { ...endpoint, [field]: value }
      : endpoint),
  }));
  const updateTargetGroup = (index, patch) => setTopology((current) => ({
    ...current,
    targetGroups: (current.targetGroups || []).map((group, groupIndex) => (
      groupIndex === index ? { ...group, ...patch } : group
    )),
  }));
  const updatePlayer = (screenIndex, playerIndex, field, value) => setTopology((current) => ({
    ...current,
    screens: current.screens.map((screen, index) => {
      if (index !== screenIndex) return screen;
      const players = [...(screen.players || [])];
      players[playerIndex] = { ...players[playerIndex], [field]: value };
      return { ...screen, players };
    }),
  }));

  const save = async () => {
    setSaving(true);
    setMessage('');
    try {
      const response = await fetch(`/api/areas/${areaId}/topology`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(topology),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Could not save topology');
      setTopology(data);
      onTopologyChange?.(data);
      setMessage('Screen and audio relationships saved.');
    } catch (error) {
      setMessage(error.message);
    } finally {
      setSaving(false);
    }
  };

  const reloadTopology = async () => {
    const response = await fetch(`/api/areas/${areaId}/topology`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.message || 'Could not reload topology');
    setTopology(data);
    onTopologyChange?.(data);
    return data;
  };

  const saveTargetGroup = async (group) => {
    setSaving(true);
    setMessage('');
    try {
      const response = await fetch(
        group.id
          ? `/api/areas/${areaId}/target-groups/${group.id}`
          : `/api/areas/${areaId}/target-groups`,
        {
          method: group.id ? 'PUT' : 'POST',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({
            name: group.name,
            description: group.description,
            screenIds: group.screenIds || [],
            audioEndpointIds: group.audioEndpointIds || [],
          }),
        }
      );
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Could not save target group');
      await reloadTopology();
      setMessage('Venue target group saved.');
    } catch (error) {
      setMessage(error.message);
    } finally {
      setSaving(false);
    }
  };

  const removeTargetGroup = async (group, index) => {
    if (!group.id) {
      setTopology((current) => ({
        ...current,
        targetGroups: (current.targetGroups || []).filter((_, groupIndex) => groupIndex !== index),
      }));
      return;
    }
    if (!window.confirm(`Delete ${group.name}?`)) return;
    setSaving(true);
    setMessage('');
    try {
      const response = await fetch(`/api/areas/${areaId}/target-groups/${group.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Could not delete target group');
      await reloadTopology();
      setMessage('Venue target group deleted.');
    } catch (error) {
      setMessage(error.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <section className="topology-setup">Loading Screen setup…</section>;
  return (
    <section className="topology-setup">
      <div className="topology-setup__heading">
        <div>
          <h3>Screens and audio</h3>
          <p>Group the physical devices staff use together. An unmanaged display can still have a connected player.</p>
        </div>
        <button type="button" className="topology-setup__primary" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save relationships'}</button>
      </div>

      <div className="topology-setup__list">
        {topology.screens.map((screen, index) => (
          <SetupPanel className="topology-setup__screen" key={screen.id || `screen-${index}`}
            defaultOpen={!screen.id}
            title={screen.name || deviceOptions.find((device) => Number(device.id) === Number(screen.televisionDeviceId))?.name || 'New Screen'}
            description={`${screen.displayKind === 'unmanaged' ? 'Unmanaged display' : 'TV'} · ${(screen.players || []).length} connected devices · Connections and HDMI ports`}>
            <div className="topology-setup__screen-topline">
              {screen.displayKind === 'unmanaged'
                ? <label>Screen name<input value={screen.name || ''} onChange={(event) => updateScreen(index, 'name', event.target.value)} placeholder="Stage Screen" /></label>
                : <label>Screen name<input value={deviceOptions.find((device) => Number(device.id) === Number(screen.televisionDeviceId))?.name || 'Select a television'} disabled /></label>}
              <label>Display type<select value={screen.displayKind || 'managed'} onChange={(event) => {
                const displayKind = event.target.value;
                updateScreen(index, 'displayKind', displayKind);
                if (displayKind === 'unmanaged') updateScreen(index, 'televisionDeviceId', '');
              }}><option value="managed">Managed TV</option><option value="unmanaged">Unmanaged TV or monitor</option></select></label>
              {screen.displayKind !== 'unmanaged' && <label>Television<select value={screen.televisionDeviceId || ''} onChange={(event) => updateScreen(index, 'televisionDeviceId', event.target.value)}><option value="">Select television</option>{deviceOptions.filter((device) => !isAudioEquipment(device) && device.type !== 'equipment').map((device) => <option key={device.id} value={device.id}>{device.name}</option>)}</select></label>}
              <button type="button" className="topology-setup__remove" onClick={() => setTopology((current) => ({ ...current, screens: current.screens.filter((_, itemIndex) => itemIndex !== index) }))}>Remove Screen</button>
            </div>
            <fieldset className="topology-setup__players">
              <legend>Connected sources and equipment</legend>
              <div className="topology-setup__player-headings" aria-hidden="true">
                <span>Equipment</span><span>TV port</span><span>Role</span><span />
              </div>
              {(screen.players || []).map((player, playerIndex) => (
                <div className="topology-setup__player" key={`player-${playerIndex}`}>
                  <select aria-label="Connected player" value={player.deviceId || player.id || ''} onChange={(event) => {
                    updatePlayer(index, playerIndex, 'deviceId', event.target.value);
                    updatePlayer(index, playerIndex, 'sourceRole', isAudioEquipment(deviceOptions.find((device) => Number(device.id) === Number(event.target.value))) ? 'audio_system' : 'video_source');
                  }}>
                    <option value="">Select source or audio equipment</option>
                    {deviceOptions.map((device) => <option key={device.id} value={device.id}>{device.name}</option>)}
                  </select>
                  <PortSelector value={player.expectedInput || ''} onChange={(value) => updatePlayer(index, playerIndex, 'expectedInput', value)} />
                  <small>{player.sourceRole === 'audio_system' ? 'Audio equipment' : player.isIgnored ? 'Ignored' : Number(player.deviceId || player.id) === Number(screen.preferredPlayerDeviceId || screen.players.find((entry) => (!entry.sourceRole || entry.sourceRole === 'video_source') && !entry.isIgnored)?.deviceId) ? 'Preferred' : 'Alternative'}</small>
                  <button type="button" className="topology-setup__remove topology-setup__icon-button" aria-label="Remove connected player" onClick={() => updateScreen(index, 'players', (screen.players || []).filter((_, itemIndex) => itemIndex !== playerIndex))}>×</button>
                </div>
              ))}
              <button type="button" className="topology-setup__add" onClick={() => updateScreen(index, 'players', [...(screen.players || []), { deviceId: '', expectedInput: '', mappingMode: 'manual', mappingConfidence: 'unknown' }])}>+ Add player</button>
            </fieldset>
            <SetupPanel title="HDMI ports and source assignment" description="Match the TV’s inputs to connected equipment">
            <HdmiInputAssignmentBoard
              areaId={areaId}
              screen={screen}
              report={screen.inputReport || tvInputReports[Number(screen.televisionDeviceId)]}
              devices={deviceOptions}
              disabled={saving}
              onEquipmentCreated={(device) => setEquipment((current) => [...current.filter((entry) => Number(entry.id) !== Number(device.id)), device])}
              onChange={(nextScreen) => setTopology((current) => ({ ...current,
                screens: current.screens.map((entry, screenIndex) => screenIndex === index ? nextScreen : entry),
              }))}
            />
            </SetupPanel>
          </SetupPanel>
        ))}
        <button type="button" className="topology-setup__add" onClick={() => setTopology((current) => ({ ...current, screens: [...current.screens, emptyScreen()] }))}>+ Add Screen</button>
      </div>

      <SetupPanel title="Equipment and app registrations" description="Add legacy equipment or link an existing device to its Suitestream app">
        <EquipmentEditor key={areaId} areaId={areaId} onEquipmentChange={handleEquipmentChange} />
        <EquipmentIdentityLinks key={`identity-${areaId}`} areaId={areaId} />
      </SetupPanel>
      <SetupPanel title="Audio endpoints" description={`${topology.audioEndpoints.length} audio routes · Follow a Screen or use an independent source`}>
        {topology.audioEndpoints.map((endpoint, index) => (
          <div className="topology-setup__row topology-setup__row--audio" key={endpoint.id || `audio-${index}`}>
            <label>Name<input value={endpoint.name} onChange={(event) => updateAudio(index, 'name', event.target.value)} placeholder="House Audio" /></label>
            <label>Endpoint source / TV output<select value={endpoint.sourceDeviceId || ''} onChange={(event) => updateAudio(index, 'sourceDeviceId', event.target.value)}><option value="">No directly managed source</option>{deviceOptions.map((device) => <option key={device.id} value={device.id}>{device.name}</option>)}</select></label>
            <label>Audio follows Screen<select value={endpoint.logicalScreenId || ''} onChange={(event) => updateAudio(index, 'logicalScreenId', event.target.value)}><option value="">Independent audio route</option>{topology.screens.filter((screen) => screen.id).map((screen) => <option key={screen.id} value={screen.id}>{screen.name} · inherit selected HDMI audio</option>)}</select></label>
            <button type="button" className="topology-setup__remove" onClick={() => setTopology((current) => ({ ...current, audioEndpoints: current.audioEndpoints.filter((_, itemIndex) => itemIndex !== index) }))}>Remove</button>
          </div>
        ))}
        <button type="button" className="topology-setup__add" onClick={() => setTopology((current) => ({ ...current, audioEndpoints: [...current.audioEndpoints, emptyAudio()] }))}>+ Add Audio Endpoint</button>
      </SetupPanel>

      <SetupPanel title="Venue target groups" description="Optional groups for campaigns and coordinated playback">
        <div>
          <h4>Venue target groups</h4>
          <p>Campaign phases target these names. Built-in groups follow your Screen and audio relationships automatically.</p>
        </div>
        {(topology.targetGroups || []).map((group, index) => {
          const issues = group.resolution?.diagnostics?.issues || [];
          return (
            <div className="topology-setup__screen" key={group.id || `target-group-${index}`}>
              <div className="topology-setup__screen-topline">
                <label>Name<input value={group.name || ''} disabled={group.isSystem} onChange={(event) => updateTargetGroup(index, { name: event.target.value })} placeholder="Bar Screens" /></label>
                <label>Description<input value={group.description || ''} disabled={group.isSystem} onChange={(event) => updateTargetGroup(index, { description: event.target.value })} placeholder="Screens used for live promotions" /></label>
                {group.isSystem
                  ? <small>Built in · updates automatically</small>
                  : <button type="button" className="topology-setup__remove" disabled={saving} onClick={() => removeTargetGroup(group, index)}>Delete</button>}
              </div>
              <fieldset className="topology-setup__players">
                <legend>Screens</legend>
                {(topology.screens || []).filter((screen) => screen.id).map((screen) => (
                  <label key={screen.id}>
                    <input
                      type="checkbox"
                      disabled={group.isSystem}
                      checked={(group.screenIds || []).includes(Number(screen.id))}
                      onChange={(event) => updateTargetGroup(index, {
                        screenIds: toggleTargetGroupMember(
                          group.screenIds,
                          screen.id,
                          event.target.checked
                        ),
                      })}
                    /> {screen.name}
                  </label>
                ))}
                <strong>Audio Endpoints</strong>
                {(topology.audioEndpoints || []).filter((endpoint) => endpoint.id).map((endpoint) => (
                  <label key={endpoint.id}>
                    <input
                      type="checkbox"
                      disabled={group.isSystem}
                      checked={(group.audioEndpointIds || []).includes(Number(endpoint.id))}
                      onChange={(event) => updateTargetGroup(index, {
                        audioEndpointIds: toggleTargetGroupMember(
                          group.audioEndpointIds,
                          endpoint.id,
                          event.target.checked
                        ),
                      })}
                    /> {endpoint.name}
                  </label>
                ))}
              </fieldset>
              {issues.length ? (
                <p className="topology-setup__message">
                  {issues.map((issue) => `${issue.name}: ${issue.issue.replace(/_/g, ' ')}`).join(' · ')}
                </p>
              ) : (
                <small>{group.resolution?.diagnostics?.compatibleCount || 0} compatible logical endpoint(s)</small>
              )}
              {!group.isSystem && (
                <button type="button" className="topology-setup__primary" disabled={saving} onClick={() => saveTargetGroup(group)}>
                  {saving ? 'Saving…' : 'Save group'}
                </button>
              )}
            </div>
          );
        })}
        <button
          type="button"
          className="topology-setup__add"
          onClick={() => setTopology((current) => ({
            ...current,
            targetGroups: [...(current.targetGroups || []), emptyTargetGroup()],
          }))}
        >
          + Add Target Group
        </button>
      </SetupPanel>
      <button type="button" disabled={saving} onClick={() => reloadTopology().catch((error) => setMessage(error.message))}>Reload saved topology (discard draft)</button>
      {topology.screens.flatMap((screen) => (screen.players || []).filter((player) => player.signalPath === 'shared_distribution').map((player) => ({ screen, player })))
        .filter((entry, index, all) => all.findIndex((other) => Number(other.player.deviceId || other.player.id) === Number(entry.player.deviceId || entry.player.id)) === index)
        .map(({ player }) => <p key={`shared-${player.deviceId || player.id}`}>Shared source {player.name || deviceOptions.find((device) => Number(device.id) === Number(player.deviceId || player.id))?.name}: {topology.screens.filter((screen) => screen.players?.some((entry) => Number(entry.deviceId || entry.id) === Number(player.deviceId || player.id))).map((screen) => `${screen.name || 'Screen'} / ${screen.players.find((entry) => Number(entry.deviceId || entry.id) === Number(player.deviceId || player.id)).expectedInput || 'input unassigned'}`).join(', ')}</p>)}
      {message && <p className="topology-setup__message" role="status">{message}</p>}
    </section>
  );
}

export default LogicalTopologySetup;
